<template>
  <div class="carousel-wrapper">
    <!-- Dynamic carousel generation based on carouselConfig -->
    <carousel :per-page="1" class="carousel" v-if="isMultiSlide">
      <slide v-for="slide in slides" :key="slide.id">
        <div
          :class="['slide-item', slide.cssClass]"
          :style="{ backgroundImage: `url(${slide.image.src})` }"
        >
          <div :class="['slide-content', { 'no-text': authType === 'login' }]">
            <div class="slide-txt">
              <!-- Step indicator (if exists) -->
              <p v-if="slide.content.step" class="slide-step">
                {{ $t(slide.content.step) }}
              </p>

              <!-- Title and subtitle -->
              <p class="slide-title">
                {{ $t(slide.content.title) }}
                <br v-if="slide.content.subtitle" />
                <strong v-if="slide.content.subtitle">
                  {{ $t(slide.content.subtitle) }}
                </strong>
              </p>

              <!-- Main text -->
              <p v-if="slide.content.text0">
                {{ $t(slide.content.text0) }}
              </p>

              <!-- Secondary text (bold) -->
              <p v-if="slide.content.text1">
                <strong>{{ $t(slide.content.text1) }}</strong>
              </p>
            </div>
          </div>
        </div>
      </slide>
    </carousel>

    <!-- Single slide carousel (login, etc.) -->
    <carousel :per-page="1" class="carousel" v-else>
      <slide v-for="slide in slides" :key="slide.id">
        <div
          :class="['slide-item', slide.cssClass]"
          :style="{ backgroundImage: `url(${slide.image.src})` }"
        >
          <div :class="['slide-content', { 'no-text': authType === 'login' }]">
            <div class="slide-txt">
              <!-- Step indicator (if exists) -->
              <p v-if="slide.content.step" class="slide-step">
                {{ $t(slide.content.step) }}
              </p>

              <!-- Title and subtitle -->
              <p class="slide-title">
                {{ $t(slide.content.title) }}
                <br v-if="slide.content.subtitle" />
                <strong v-if="slide.content.subtitle">
                  {{ $t(slide.content.subtitle) }}
                </strong>
              </p>

              <!-- Main text -->
              <p v-if="slide.content.text0">
                {{ $t(slide.content.text0) }}
              </p>

              <!-- Secondary text (bold) -->
              <p v-if="slide.content.text1">
                <strong>{{ $t(slide.content.text1) }}</strong>
              </p>
            </div>
          </div>
        </div>
      </slide>
    </carousel>

    <div class="language-wrapper">
      <div
        class="custom-dropdown"
        :class="{ open: dropdownOpen }"
        ref="dropdown"
      >
        <button
          type="button"
          class="dropdown-trigger"
          @click="toggleDropdown"
          :aria-expanded="dropdownOpen"
          aria-haspopup="true"
          :aria-label="$t('auth.language.select')"
        >
          <p class="locale">{{ this.$i18n.locale.toUpperCase() }}</p>
          <i class="material-icons">keyboard_arrow_down</i>
        </button>
        <ul v-if="dropdownOpen" class="dropdown-menu select-lang" role="menu">
          <li
            v-for="lang in locales"
            :key="lang"
            :class="[lang, { active: lang === $i18n.locale }]"
            @click.stop="changeLocale(lang)"
            role="menuitem"
            :aria-selected="lang === $i18n.locale"
            tabindex="0"
            @keydown.enter="changeLocale(lang)"
            @keydown.space.prevent="changeLocale(lang)"
          >
            <p>{{ lang.toUpperCase() }}</p>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { Carousel, Slide } from 'vue-carousel'
import { getCarouselConfig } from './constants/carouselConfig.js'

export default {
  name: 'component-carousel',

  components: {
    Carousel,
    Slide
  },

  props: {
    authType: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      locales: ['en', 'fr', 'de', 'es', 'pt', 'it'],
      dropdownOpen: false
    }
  },

  computed: {
    currentLocale() {
      return this.$i18n.locale
    },

    /**
     * Get carousel configuration based on authType prop
     */
    carouselConfig() {
      return getCarouselConfig(this.authType)
    },

    /**
     * Get slides for current auth type
     */
    slides() {
      return this.carouselConfig.slides
    },

    /**
     * Check if current authType should show multiple slides
     */
    isMultiSlide() {
      return this.authType === 'signup'
    }
  },

  methods: {
    /**
     * Change application locale and persist to localStorage
     * @param {string} lang - Language code (en, fr, de, es, pt)
     */
    changeLocale(lang) {
      if (lang === this.$i18n.locale) return

      localStorage.setItem('language', lang)
      this.$i18n.locale = lang
      this.dropdownOpen = false

      // Emit event to parent if needed for analytics
      this.$emit('locale-changed', { from: this.currentLocale, to: lang })
    },

    handleClickOutside(event) {
      if (this.$refs.dropdown && !this.$refs.dropdown.contains(event.target)) {
        this.dropdownOpen = false
      }
    },

    toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen
    },

    handleKeydown(event) {
      if (event.key === 'Escape' && this.dropdownOpen) {
        this.dropdownOpen = false
      }
    }
  },

  mounted() {
    document.addEventListener('click', this.handleClickOutside)
    document.addEventListener('keydown', this.handleKeydown)
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
    document.removeEventListener('keydown', this.handleKeydown)
  }
}
</script>
